import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { ENDPOINT } from 'src/constants/api';

import { loginCompleted, logoutCompleted } from '../slices/auth';
import { setInventory } from '../slices/inventory';

export const setHeaders = (headers: Headers, { getState }: { getState: () => unknown }) => {
  const { auth } = getState() as IReducerStates;
  const { superAdminToken, token, accountId } = getTokenAndAccountId(auth);

  headers.set('Accept', 'application/json');
  if (!superAdminToken && !token) {
    return headers;
  }

  if (token && !superAdminToken) {
    headers.set('Authorization', `Bearer ${token}`);
  } else if (superAdminToken) {
    headers.set('Authorization', `Bearer ${superAdminToken}`);
  }

  if (accountId) {
    headers.set('X-Account', accountId.toString());
  }

  headers.set('X-Language', auth.language);

  return headers;
};

const getTokenAndAccountId = (auth: IAuthState) => ({
  superAdminToken: auth.superAdminToken || '',
  token: auth.currentUserToken || '',
  accountId: auth.currentAccount?.id || 0,
});

// Define base query with authorization header
export const baseQuery = fetchBaseQuery({
  baseUrl: ENDPOINT,
  mode: 'cors',
  prepareHeaders: setHeaders,
});

const mutex = new Mutex();

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  const { auth, inventory } = api.getState() as IReducerStates;

  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await baseQuery(
          {
            url: '/auth/refresh',
            method: 'POST',
            body: {},
          },
          api,
          extraOptions
        );
        if (refreshResult.data) {
          if (auth.currentUser) {
            api.dispatch(
              loginCompleted(
                // @ts-ignore
                {
                  token: refreshResult.data.data.token,
                  user: auth.superAdminUser || auth.currentUser,
                  isRefresh: true,
                }
              )
            );
          } else {
            api.dispatch(
              loginCompleted(
                // @ts-ignore
                {
                  token: refreshResult.data.data.token,
                  user: auth.superAdminUser || auth.superAdminUser,
                  isRefresh: true,
                }
              )
            );
          }
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logoutCompleted());
          api.dispatch(setInventory({ ...inventory, data: [] }));
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export const baseApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});
